import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  RefreshCw,
  Trash2,
  ShieldCheck,
  ShieldAlert,
  Database,
  TestTube,
  ClipboardCopy,
  History,
  Undo2,
  Save,
  Download,
  Upload,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const copyToClipboard = (text) => {
  try {
    navigator.clipboard.writeText(text);
    toast.success('Backup ID copied to clipboard');
  } catch {
    toast.error('Could not copy — please copy manually');
  }
};

const SectionCard = ({ icon: Icon, title, description, children, accent = 'slate' }) => (
  <Card className="border-2" data-testid={`cleanup-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardHeader className={`bg-${accent}-50 border-b`}>
      <CardTitle className="flex items-center gap-2 text-lg">
        <Icon className="h-5 w-5" />
        {title}
      </CardTitle>
      <p className="text-sm text-slate-600 mt-1">{description}</p>
    </CardHeader>
    <CardContent className="pt-6 space-y-4">{children}</CardContent>
  </Card>
);

const BackupIdBox = ({ id }) =>
  id ? (
    <div
      className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm"
      data-testid="cleanup-backup-id-box"
    >
      <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
      <span className="text-emerald-900 font-medium">Backup created:</span>
      <code className="text-xs bg-white border px-2 py-0.5 rounded select-all">{id}</code>
      <Button
        size="sm"
        variant="ghost"
        className="ml-auto"
        onClick={() => copyToClipboard(id)}
        data-testid="cleanup-copy-backup-id-btn"
      >
        <ClipboardCopy className="h-4 w-4" />
      </Button>
    </div>
  ) : null;

export default function AdminCleanup() {
  const { user } = useAuth();

  // Budget dupes state
  const [dupeReport, setDupeReport] = useState(null);
  const [dupeLoading, setDupeLoading] = useState(false);
  const [dupeCleanupResult, setDupeCleanupResult] = useState(null);
  const [dupeConfirmText, setDupeConfirmText] = useState('');
  const [dupeDialogOpen, setDupeDialogOpen] = useState(false);
  const [dupeRunning, setDupeRunning] = useState(false);

  // Test artifacts state
  const [testReport, setTestReport] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testPurgeResult, setTestPurgeResult] = useState(null);
  const [testConfirmText, setTestConfirmText] = useState('');
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testRunning, setTestRunning] = useState(false);

  // Backup history state
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Restore state
  const [restoreTarget, setRestoreTarget] = useState(null); // { backup_id, kind, created_at, ... }
  const [restoreConfirmText, setRestoreConfirmText] = useState('');
  const [restoreRunning, setRestoreRunning] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);

  // Manual backup state
  const [manualLabel, setManualLabel] = useState('');
  const [manualRunning, setManualRunning] = useState(false);
  const [manualResult, setManualResult] = useState(null);

  // Restore-from-file state
  const [fileToRestore, setFileToRestore] = useState(null);
  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [fileConfirmText, setFileConfirmText] = useState('');
  const [fileRunning, setFileRunning] = useState(false);
  const [fileResult, setFileResult] = useState(null);

  if (!user?.is_super_admin) {
    return (
      <div className="max-w-3xl mx-auto py-10" data-testid="cleanup-noaccess">
        <Card>
          <CardContent className="pt-6 flex items-center gap-3">
            <ShieldAlert className="h-5 w-5 text-rose-600" />
            <p className="text-sm">
              This page is restricted to Super Admins. Please contact your administrator if you need
              access.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const loadDupeReport = async () => {
    setDupeLoading(true);
    try {
      const res = await axios.get(`${API}/admin/budget-dupes/report`, { withCredentials: true });
      setDupeReport(res.data);
      toast.success('Budget duplicate report loaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load report');
    } finally {
      setDupeLoading(false);
    }
  };

  const runDupeCleanup = async () => {
    setDupeRunning(true);
    try {
      const res = await axios.post(
        `${API}/admin/budget-dupes/cleanup?confirm=YES`,
        null,
        { withCredentials: true }
      );
      setDupeCleanupResult(res.data);
      toast.success(res.data?.message || 'Cleanup complete');
      setDupeDialogOpen(false);
      setDupeConfirmText('');
      // refresh the report so the user sees zero dupes
      await loadDupeReport();
      await loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cleanup failed');
    } finally {
      setDupeRunning(false);
    }
  };

  const loadTestReport = async () => {
    setTestLoading(true);
    try {
      const res = await axios.get(`${API}/admin/test-artifacts/report`, { withCredentials: true });
      setTestReport(res.data);
      toast.success('Test artifact report loaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load report');
    } finally {
      setTestLoading(false);
    }
  };

  const runTestPurge = async () => {
    setTestRunning(true);
    try {
      const res = await axios.post(
        `${API}/admin/test-artifacts/purge?confirm=YES`,
        null,
        { withCredentials: true }
      );
      setTestPurgeResult(res.data);
      toast.success(res.data?.message || 'Purge complete');
      setTestDialogOpen(false);
      setTestConfirmText('');
      await loadTestReport();
      await loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Purge failed');
    } finally {
      setTestRunning(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/admin/cleanup-history`, { withCredentials: true });
      setHistory(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const fmtDate = (v) => {
    if (!v) return '—';
    try {
      const d = new Date(v);
      return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return String(v);
    }
  };

  const createManualBackup = async () => {
    setManualRunning(true);
    try {
      const url = `${API}/admin/backups/manual${manualLabel ? `?label=${encodeURIComponent(manualLabel)}` : ''}`;
      const res = await axios.post(url, null, { withCredentials: true });
      setManualResult(res.data);
      toast.success(res.data?.message || 'Backup created');
      setManualLabel('');
      await loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Backup failed');
    } finally {
      setManualRunning(false);
    }
  };

  const downloadBackup = async (backupId, label) => {
    try {
      const res = await axios.get(`${API}/admin/backups/${backupId}/download`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup-${label || backupId}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Download failed');
    }
  };

  const runRestoreFromFile = async () => {
    if (!fileToRestore) return;
    setFileRunning(true);
    try {
      const fd = new FormData();
      fd.append('file', fileToRestore);
      const res = await axios.post(
        `${API}/admin/backups/restore-from-file?confirm=YES`,
        fd,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      setFileResult(res.data);
      toast.success(res.data?.message || 'Restore complete');
      setFileDialogOpen(false);
      setFileConfirmText('');
      setFileToRestore(null);
      await loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore from file failed');
    } finally {
      setFileRunning(false);
    }
  };

  const runRestore = async () => {    if (!restoreTarget) return;
    setRestoreRunning(true);
    try {
      const res = await axios.post(
        `${API}/admin/cleanup-history/${restoreTarget.backup_id}/restore?confirm=YES`,
        null,
        { withCredentials: true }
      );
      setRestoreResult({ ...res.data, backup_id: restoreTarget.backup_id });
      toast.success(res.data?.message || 'Restore complete');
      setRestoreTarget(null);
      setRestoreConfirmText('');
      // refresh underlying reports so admin can see the recovered counts
      if (restoreTarget.kind === 'budget_dupes') {
        await loadDupeReport();
      } else {
        await loadTestReport();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoreRunning(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 py-6" data-testid="admin-cleanup-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Database Cleanup</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-3xl">
          Run safe, in-app cleanups for historical budget duplicates and test artifacts. Every
          destructive action takes a backup first and records a Backup ID you can use to restore.
          <span className="block mt-1 text-rose-700 font-medium">
            Only Super Admins can run these. Always run the Report first, then Cleanup.
          </span>
        </p>
      </div>

      {/* ─── Budget Duplicates ──────────────────────────────────────────── */}
      <SectionCard
        icon={Database}
        title="Budget Duplicates"
        description="Finds rows where (Department + Budget Head + Fiscal Year) appear more than once and keeps the best one (most recent with values intact)."
      >
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={loadDupeReport}
            disabled={dupeLoading}
            variant="outline"
            data-testid="cleanup-dupe-report-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${dupeLoading ? 'animate-spin' : ''}`} />
            {dupeLoading ? 'Loading…' : 'Show Duplicate Report'}
          </Button>

          <Button
            onClick={() => setDupeDialogOpen(true)}
            disabled={!dupeReport || dupeReport.total_deletes_planned === 0}
            variant="destructive"
            data-testid="cleanup-dupe-run-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clean Duplicates
          </Button>

          {dupeReport && (
            <Badge variant="secondary" className="ml-auto" data-testid="cleanup-dupe-summary">
              {dupeReport.total_duplicate_groups} group(s) • {dupeReport.total_deletes_planned} row(s) to delete
            </Badge>
          )}
        </div>

        {dupeReport && dupeReport.total_duplicate_groups === 0 && (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-3">
            ✓ No duplicates found — your database is clean.
          </div>
        )}

        {dupeReport && dupeReport.groups?.length > 0 && (
          <div className="border rounded-md overflow-x-auto" data-testid="cleanup-dupe-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Department</TableHead>
                  <TableHead>Budget Head</TableHead>
                  <TableHead>FY</TableHead>
                  <TableHead className="text-right">Copies</TableHead>
                  <TableHead>Keep (Winner)</TableHead>
                  <TableHead>Delete (Losers)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dupeReport.groups.map((g, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{g.department}</TableCell>
                    <TableCell>{g.budget_head}</TableCell>
                    <TableCell>{g.fiscal_year}</TableCell>
                    <TableCell className="text-right">{g.n_copies}</TableCell>
                    <TableCell className="text-xs">
                      <div className="truncate max-w-[200px]" title={g.winner?.id}>
                        ₹{(g.winner?.allocated_amount || 0).toLocaleString()}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-rose-700">
                      {g.losers.length} row(s)
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {dupeCleanupResult && (
          <div className="space-y-2">
            <div className="text-sm bg-blue-50 border border-blue-200 rounded-md p-3">
              <strong>Result:</strong> {dupeCleanupResult.message}
            </div>
            <BackupIdBox id={dupeCleanupResult.snapshot_backup_id} />
            {dupeCleanupResult.tip && (
              <p className="text-xs text-slate-500 italic">{dupeCleanupResult.tip}</p>
            )}
          </div>
        )}
      </SectionCard>

      {/* ─── Test Artifacts ─────────────────────────────────────────────── */}
      <SectionCard
        icon={TestTube}
        title="Test Artifacts"
        description="Finds and removes data left behind by automated tests — test users (e.g. *@example.com), test divisions (codes DT/NA), test policies and claims with TEST_ prefixes."
      >
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={loadTestReport}
            disabled={testLoading}
            variant="outline"
            data-testid="cleanup-test-report-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${testLoading ? 'animate-spin' : ''}`} />
            {testLoading ? 'Loading…' : 'Show Test Data Report'}
          </Button>

          <Button
            onClick={() => setTestDialogOpen(true)}
            disabled={!testReport || testReport.summary?.total === 0}
            variant="destructive"
            data-testid="cleanup-test-run-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Purge Test Data
          </Button>

          {testReport && (
            <Badge variant="secondary" className="ml-auto" data-testid="cleanup-test-summary">
              {testReport.summary?.total} test row(s) found
            </Badge>
          )}
        </div>

        {testReport && testReport.summary?.total === 0 && (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-3">
            ✓ No test artifacts found — your database is clean.
          </div>
        )}

        {testReport && testReport.summary?.total > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="cleanup-test-counts">
            {Object.entries(testReport.summary)
              .filter(([k]) => k !== 'total')
              .map(([k, v]) => (
                <div key={k} className="border rounded-md p-3 bg-slate-50">
                  <p className="text-xs text-slate-500 capitalize">{k.replace(/_count$/, '').replace(/_/g, ' ')}</p>
                  <p className="text-2xl font-bold">{v}</p>
                </div>
              ))}
          </div>
        )}

        {testPurgeResult && (
          <div className="space-y-2">
            <div className="text-sm bg-blue-50 border border-blue-200 rounded-md p-3">
              <strong>Result:</strong> {testPurgeResult.message}
            </div>
            <BackupIdBox id={testPurgeResult.snapshot_backup_id} />
          </div>
        )}
      </SectionCard>

      {/* ─── Manual Backup (download-on-demand snapshot) ──────────────── */}
      <SectionCard
        icon={Save}
        title="Manual Backup"
        description="Take a full point-in-time snapshot of every key business collection (budgets, POs, invoices, vendors, payments, divisions, claims, etc.). Use this before deploys or risky data changes."
      >
        <div className="flex flex-wrap items-center gap-3">
          <Input
            value={manualLabel}
            onChange={(e) => setManualLabel(e.target.value)}
            placeholder="Optional label (e.g. 'Pre-March-deploy')"
            className="max-w-xs"
            data-testid="cleanup-manual-label-input"
          />
          <Button
            onClick={createManualBackup}
            disabled={manualRunning}
            data-testid="cleanup-manual-create-btn"
          >
            <Save className={`h-4 w-4 mr-2 ${manualRunning ? 'animate-spin' : ''}`} />
            {manualRunning ? 'Backing up…' : 'Create Manual Backup'}
          </Button>
        </div>

        {manualResult && (
          <div className="space-y-2">
            <div className="text-sm bg-emerald-50 border border-emerald-200 rounded-md p-3">
              <strong>Snapshot saved:</strong> {manualResult.message}
            </div>
            <BackupIdBox id={manualResult.backup_id} />
            <Button
              size="sm"
              variant="outline"
              onClick={() => downloadBackup(manualResult.backup_id, manualResult.label)}
              data-testid="cleanup-manual-download-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Download backup JSON
            </Button>
          </div>
        )}
      </SectionCard>

      {/* ─── Restore from File (disaster recovery) ─────────────────────── */}
      <SectionCard
        icon={Upload}
        title="Restore from File"
        description="Disaster recovery — upload a backup JSON file (one you previously downloaded) and restore the data. Idempotent: only rows missing from the database are inserted; existing rows are never overwritten."
      >
        <div className="flex flex-wrap items-center gap-3">
          <Input
            type="file"
            accept="application/json,.json"
            onChange={(e) => {
              setFileToRestore(e.target.files?.[0] || null);
              setFileResult(null);
            }}
            className="max-w-md"
            data-testid="cleanup-file-restore-input"
          />
          <Button
            disabled={!fileToRestore}
            onClick={() => setFileDialogOpen(true)}
            variant="default"
            data-testid="cleanup-file-restore-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Restore from this file
          </Button>
          {fileToRestore && (
            <span className="text-xs text-slate-500">
              {fileToRestore.name} · {(fileToRestore.size / 1024).toFixed(1)} KB
            </span>
          )}
        </div>

        {fileResult && (
          <div className="text-sm bg-emerald-50 border border-emerald-200 rounded-md p-3 space-y-1" data-testid="cleanup-file-restore-result">
            <div><strong>Restored:</strong> {fileResult.message}</div>
            <div className="text-xs text-slate-700">
              From: <code className="bg-white border px-1 rounded">{fileResult.filename}</code> ({(fileResult.size_bytes / 1024).toFixed(1)} KB)
            </div>
            {fileResult.rejected_collections?.length > 0 && (
              <div className="text-xs text-amber-700">
                Note: skipped unrecognised collections — {fileResult.rejected_collections.join(', ')}
              </div>
            )}
          </div>
        )}
      </SectionCard>

      {/* ─── Backup History ─────────────────────────────────────────────── */}
      <SectionCard
        icon={History}
        title="Backup History"
        description="Every cleanup automatically saves a snapshot. Use this list to find the Backup ID of any past cleanup — useful if a row needs to be restored."
      >
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={loadHistory}
            disabled={historyLoading}
            variant="outline"
            data-testid="cleanup-history-load-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${historyLoading ? 'animate-spin' : ''}`} />
            {historyLoading ? 'Loading…' : 'Show Backup History'}
          </Button>
          {history && (
            <Badge variant="secondary" className="ml-auto" data-testid="cleanup-history-count">
              {history.total} backup(s) on file
            </Badge>
          )}
        </div>

        {history && history.total === 0 && (
          <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3">
            No cleanup snapshots yet. Backup IDs will appear here every time a cleanup runs.
          </div>
        )}

        {history && history.entries?.length > 0 && (
          <div className="border rounded-md overflow-x-auto" data-testid="cleanup-history-table">
            <Table>              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Rows backed up</TableHead>
                  <TableHead>Backup ID</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.entries.map((e, idx) => (
                  <TableRow key={e.backup_id || idx} data-testid={`cleanup-history-row-${idx}`}>
                    <TableCell className="text-xs">{fmtDate(e.created_at)}</TableCell>
                    <TableCell>
                      {e.kind === 'budget_dupes' ? (
                        <Badge variant="secondary" className="bg-amber-100 text-amber-900">Budget dedupe</Badge>
                      ) : e.kind === 'manual' ? (
                        <Badge variant="secondary" className="bg-emerald-100 text-emerald-900">Manual</Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-purple-100 text-purple-900">Test purge</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{e.actor_email || '—'}</TableCell>
                    <TableCell className="text-xs">
                      {e.kind === 'budget_dupes'
                        ? `${e.row_count ?? '—'} budget rows`
                        : e.kind === 'manual'
                        ? `${e.row_count ?? '—'} rows · ${e.collections_count ?? '—'} cols`
                        : `${e.total_rows ?? '—'} test rows`}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <code className="text-[11px] bg-slate-50 border px-2 py-0.5 rounded select-all max-w-[200px] truncate">
                          {e.backup_id}
                        </code>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          onClick={() => copyToClipboard(e.backup_id)}
                          data-testid={`cleanup-history-copy-${idx}`}
                          title="Copy Backup ID"
                        >
                          <ClipboardCopy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadBackup(e.backup_id, e.label || e.kind)}
                          data-testid={`cleanup-history-download-${idx}`}
                          title="Download backup as JSON"
                        >
                          <Download className="h-3.5 w-3.5 mr-1" />
                          Download
                        </Button>
                        {(e.kind === 'budget_dupes' || e.kind === 'test_artifacts' || e.kind === 'manual') && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                            onClick={() => {
                              setRestoreTarget(e);
                              setRestoreConfirmText('');
                            }}
                            data-testid={`cleanup-history-restore-${idx}`}
                          >
                            <Undo2 className="h-3.5 w-3.5 mr-1" />
                            Restore
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {restoreResult && (
          <div className="text-sm bg-emerald-50 border border-emerald-200 rounded-md p-3" data-testid="cleanup-restore-result">
            <strong>Restored:</strong> {restoreResult.message}
            <div className="mt-1 text-xs text-slate-700">
              Backup ID: <code className="bg-white border px-1 rounded">{restoreResult.backup_id}</code>
            </div>
          </div>
        )}
      </SectionCard>

      {/* ─── Confirm dialogs ────────────────────────────────────────────── */}
      <AlertDialog open={dupeDialogOpen} onOpenChange={setDupeDialogOpen}>
        <AlertDialogContent data-testid="cleanup-dupe-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm budget duplicate cleanup</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                This will delete <strong>{dupeReport?.total_deletes_planned ?? 0}</strong> duplicate
                budget row(s). A full snapshot of <em>all</em> budget rows is saved first so any
                accidental data loss can be reversed.
              </span>
              <span className="block">
                Type <code className="bg-slate-100 px-1 rounded">CLEAN</code> below to confirm.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <Label htmlFor="dupe-confirm" className="sr-only">Confirmation</Label>
            <Input
              id="dupe-confirm"
              value={dupeConfirmText}
              onChange={(e) => setDupeConfirmText(e.target.value)}
              placeholder="Type CLEAN to confirm"
              data-testid="cleanup-dupe-confirm-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setDupeConfirmText('')}
              data-testid="cleanup-dupe-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={dupeConfirmText !== 'CLEAN' || dupeRunning}
              onClick={runDupeCleanup}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="cleanup-dupe-confirm-btn"
            >
              {dupeRunning ? 'Cleaning…' : 'Delete duplicates'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <AlertDialogContent data-testid="cleanup-test-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm test data purge</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                This will delete <strong>{testReport?.summary?.total ?? 0}</strong> test row(s)
                across users, divisions, policies, claims and audit logs. A snapshot is saved before
                deletion.
              </span>
              <span className="block">
                Type <code className="bg-slate-100 px-1 rounded">PURGE</code> below to confirm.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <Label htmlFor="test-confirm" className="sr-only">Confirmation</Label>
            <Input
              id="test-confirm"
              value={testConfirmText}
              onChange={(e) => setTestConfirmText(e.target.value)}
              placeholder="Type PURGE to confirm"
              data-testid="cleanup-test-confirm-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setTestConfirmText('')}
              data-testid="cleanup-test-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={testConfirmText !== 'PURGE' || testRunning}
              onClick={runTestPurge}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="cleanup-test-confirm-btn"
            >
              {testRunning ? 'Purging…' : 'Delete test data'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
        <AlertDialogContent data-testid="cleanup-restore-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore from backup</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                This will re-insert rows from snapshot
                {' '}
                <code className="bg-slate-100 px-1 rounded text-[11px]">{restoreTarget?.backup_id}</code>
                {' '}
                taken on <strong>{fmtDate(restoreTarget?.created_at)}</strong>.
              </span>
              <span className="block">
                <strong>Safe behaviour:</strong> existing rows with the same ID are never overwritten — only rows
                missing from the current database are restored. Running this twice is harmless.
              </span>
              <span className="block">
                Type <code className="bg-slate-100 px-1 rounded">RESTORE</code> below to confirm.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <Label htmlFor="restore-confirm" className="sr-only">Confirmation</Label>
            <Input
              id="restore-confirm"
              value={restoreConfirmText}
              onChange={(e) => setRestoreConfirmText(e.target.value)}
              placeholder="Type RESTORE to confirm"
              data-testid="cleanup-restore-confirm-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => { setRestoreTarget(null); setRestoreConfirmText(''); }}
              data-testid="cleanup-restore-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={restoreConfirmText !== 'RESTORE' || restoreRunning}
              onClick={runRestore}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="cleanup-restore-confirm-btn"
            >
              {restoreRunning ? 'Restoring…' : 'Restore now'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={fileDialogOpen} onOpenChange={setFileDialogOpen}>
        <AlertDialogContent data-testid="cleanup-file-restore-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore from backup file</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                You are about to restore data from the file{' '}
                <code className="bg-slate-100 px-1 rounded text-[11px]">
                  {fileToRestore?.name}
                </code>{' '}
                ({fileToRestore ? (fileToRestore.size / 1024).toFixed(1) : 0} KB).
              </span>
              <span className="block">
                <strong>Safe behaviour:</strong> existing rows with matching IDs are never overwritten — only missing rows are inserted. Running this twice is a no-op.
              </span>
              <span className="block">
                Type <code className="bg-slate-100 px-1 rounded">RESTORE</code> below to confirm.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <Label htmlFor="file-confirm" className="sr-only">Confirmation</Label>
            <Input
              id="file-confirm"
              value={fileConfirmText}
              onChange={(e) => setFileConfirmText(e.target.value)}
              placeholder="Type RESTORE to confirm"
              data-testid="cleanup-file-restore-confirm-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => { setFileConfirmText(''); }}
              data-testid="cleanup-file-restore-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={fileConfirmText !== 'RESTORE' || fileRunning}
              onClick={runRestoreFromFile}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="cleanup-file-restore-confirm-btn"
            >
              {fileRunning ? 'Restoring…' : 'Restore from file'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
